package br.com.htms.inspeccaocampo.data

import kotlin.math.abs
import kotlin.math.roundToLong

/** Recorte dos números do REM: só HTMS, só subcontratados ou os dois somados (bloco 3 do Excel). */
enum class RemVisao(val rotulo: String) {
    CONSOLIDADO("HTMS + Subcontratados"),
    HTMS("HTMS"),
    SUBCONTRATADOS("Subcontratados")
}

/** Metas dos indicadores. O administrador ajusta na aba Administração. */
data class RemMetas(
    val tfsa: Double = 7.0,
    val tfca: Double = 4.0,
    val tg: Double = 90.0,
    val treinamento: Double = 0.03,
    val absenteismo: Double = 0.025
) {
    fun json(): String =
        """{"tfsa":$tfsa,"tfca":$tfca,"tg":$tg,"treinamento":$treinamento,"absenteismo":$absenteismo}"""
}

fun remMetasFromJson(raw: String): RemMetas {
    val o = runCatching { JsonMap.parse(raw) }.getOrNull() ?: return RemMetas()
    return RemMetas(
        tfsa = o.double("tfsa") ?: 7.0,
        tfca = o.double("tfca") ?: 4.0,
        tg = o.double("tg") ?: 90.0,
        treinamento = o.double("treinamento") ?: 0.03,
        absenteismo = o.double("absenteismo") ?: 0.025
    )
}

/** Números de entrada de um mês (colunas B–S do Excel), já somados quando há várias empresas. */
data class RemValores(
    val colaboradores: Double = 0.0,
    val hher: Double = 0.0,
    val hhTreinamento: Double = 0.0,
    val horasAbsenteismo: Double = 0.0,
    val cat: Double = 0.0,
    val lesaoCom: Double = 0.0,
    val lesaoSem: Double = 0.0,
    val lesaoAmb: Double = 0.0,
    val diasPerdidos: Double = 0.0,
    val diasTransportados: Double = 0.0,
    val diasDebitados: Double = 0.0,
    val trajCom: Double = 0.0,
    val trajSem: Double = 0.0,
    val trajAmb: Double = 0.0,
    val trajDiasPerdidos: Double = 0.0,
    val trajDiasTransportados: Double = 0.0,
    val trajDiasDebitados: Double = 0.0,
    val danos: Double = 0.0,
    val preenchido: Boolean = false
) {
    val acidentesLesao: Double get() = lesaoCom + lesaoSem + lesaoAmb
    val acidentesTrajeto: Double get() = trajCom + trajSem + trajAmb

    operator fun plus(o: RemValores) = RemValores(
        colaboradores + o.colaboradores, hher + o.hher, hhTreinamento + o.hhTreinamento,
        horasAbsenteismo + o.horasAbsenteismo, cat + o.cat,
        lesaoCom + o.lesaoCom, lesaoSem + o.lesaoSem, lesaoAmb + o.lesaoAmb,
        diasPerdidos + o.diasPerdidos, diasTransportados + o.diasTransportados, diasDebitados + o.diasDebitados,
        trajCom + o.trajCom, trajSem + o.trajSem, trajAmb + o.trajAmb,
        trajDiasPerdidos + o.trajDiasPerdidos, trajDiasTransportados + o.trajDiasTransportados,
        trajDiasDebitados + o.trajDiasDebitados, danos + o.danos,
        preenchido || o.preenchido
    )
}

/**
 * Indicadores de um mês. Taxas nulas quando o mês não tem HHER (o Excel mostra 0; o gráfico pula o ponto).
 * Acumulados contam de janeiro até o mês.
 */
data class RemIndicadores(
    val mes: Int,
    val valores: RemValores,
    val tfsaMes: Double?,
    val tfsaAcum: Double?,
    val tfcaMes: Double?,
    val tfcaAcum: Double?,
    val tgAcum: Double?,
    val absenteismo: Double?,
    val treinamento: Double?
)

data class RemResumoAno(
    val total: RemValores,
    val mesesComDados: Int,
    val mediaColaboradores: Double,
    val tfsa: Double?,
    val tfca: Double?,
    val tg: Double?,
    val absenteismo: Double?,
    val treinamento: Double?,
    val ultimoMes: Int?
)

object RemCalc {
    private fun n(v: Double?) = v ?: 0.0

    private fun RemOcorrenciasBreakdown.algum() = listOf(
        comAfastamento, semAfastamento, simplesAtendimentoAmbulatorial, diasPerdidosNoMes, diasTransportados, diasDebitados
    ).any { it != null }

    private fun deFlat(
        colab: Double?, hher: Double?, trein: Double?, abs: Double?, cat: Double?,
        lesao: RemOcorrenciasBreakdown, traj: RemOcorrenciasBreakdown, danos: Double?
    ) = RemValores(
        n(colab), n(hher), n(trein), n(abs), n(cat),
        n(lesao.comAfastamento), n(lesao.semAfastamento), n(lesao.simplesAtendimentoAmbulatorial),
        n(lesao.diasPerdidosNoMes), n(lesao.diasTransportados), n(lesao.diasDebitados),
        n(traj.comAfastamento), n(traj.semAfastamento), n(traj.simplesAtendimentoAmbulatorial),
        n(traj.diasPerdidosNoMes), n(traj.diasTransportados), n(traj.diasDebitados),
        n(danos),
        preenchido = listOf(colab, hher, trein, abs, cat, danos).any { it != null } || lesao.algum() || traj.algum()
    )

    /** Valores de um mês; em SUBCONTRATADOS soma todas as empresas (igual à linha mensal SUB do Excel). */
    fun valores(p: RemMonthPayload, abr: RemAbrangencia): RemValores =
        if (abr == RemAbrangencia.SUBCONTRATADOS) {
            p.empresasBlocosNormalizados()
                .map { deFlat(it.numeroColaboradores, it.hher, it.homemHoraTreinamento, it.ausencias.somaHoras() ?: it.horaAbsenteismoMedico, it.numeroCat, it.ocorrenciasComLesao, it.ocorrenciasTrajeto, it.danosPropriedade) }
                .fold(RemValores()) { a, b -> a + b }
        } else {
            deFlat(p.numeroColaboradores, p.hher, p.homemHoraTreinamento, p.ausencias.somaHoras() ?: p.horaAbsenteismoMedico, p.numeroCat, p.ocorrenciasComLesao, p.ocorrenciasTrajeto, p.danosPropriedade)
        }

    /**
     * Meses do dossiê como estão na tela: o que já foi salvo mais o rascunho do mês aberto
     * (na aba de empresa ativa os campos do formulário ainda não voltaram para o bloco).
     */
    fun mesesAtuais(f: FormRemSeguranca): List<RemMonthRow> {
        if (f.dossierId.isNullOrBlank()) return f.monthsCache
        val p = f.payload
        val atual = if (f.abrangencia == RemAbrangencia.SUBCONTRATADOS) {
            val i = f.empresaTabIndex
            p.withBlocoAt(
                i,
                p.blocoAt(i).copy(
                    numeroColaboradores = p.numeroColaboradores,
                    hher = p.hher,
                    homemHoraTreinamento = p.homemHoraTreinamento,
                    horaAbsenteismoMedico = p.horaAbsenteismoMedico,
                    numeroCat = p.numeroCat,
                    ocorrenciasComLesao = p.ocorrenciasComLesao,
                    ocorrenciasTrajeto = p.ocorrenciasTrajeto,
                    danosPropriedade = p.danosPropriedade,
                    nomesColaboradores = p.nomesColaboradores,
                    ausencias = p.ausencias
                )
            )
        } else p
        val salvo = f.monthsCache.find { it.abrangencia == f.abrangencia && it.mes == f.mes }
        return f.monthsCache.filterNot { it.abrangencia == f.abrangencia && it.mes == f.mes } +
            RemMonthRow(id = salvo?.id, abrangencia = f.abrangencia, mes = f.mes, payload = atual, updatedAt = salvo?.updatedAt)
    }

    fun valoresMes(meses: List<RemMonthRow>, visao: RemVisao, mes: Int): RemValores {
        fun de(abr: RemAbrangencia) = meses.find { it.abrangencia == abr && it.mes == mes }
            ?.let { valores(it.payload, abr) } ?: RemValores()
        return when (visao) {
            RemVisao.HTMS -> de(RemAbrangencia.HTMS)
            RemVisao.SUBCONTRATADOS -> de(RemAbrangencia.SUBCONTRATADOS)
            RemVisao.CONSOLIDADO -> de(RemAbrangencia.HTMS) + de(RemAbrangencia.SUBCONTRATADOS)
        }
    }

    private fun taxa(eventos: Double, hher: Double) = if (hher > 0) eventos * 1_000_000 / hher else null

    /** 12 meses com as mesmas fórmulas da planilha corrigida (colunas T–Z). */
    fun serie(meses: List<RemMonthRow>, visao: RemVisao): List<RemIndicadores> {
        var acum = RemValores()
        return (1..12).map { m ->
            val v = valoresMes(meses, visao, m)
            acum += v
            val temHher = v.hher > 0
            RemIndicadores(
                mes = m,
                valores = v,
                tfsaMes = taxa(v.lesaoSem, v.hher),
                tfsaAcum = if (temHher) taxa(acum.lesaoSem, acum.hher) else null,
                tfcaMes = taxa(v.lesaoCom, v.hher),
                tfcaAcum = if (temHher) taxa(acum.lesaoCom, acum.hher) else null,
                tgAcum = if (temHher) taxa(acum.diasPerdidos, acum.hher) else null,
                absenteismo = if (temHher) v.horasAbsenteismo / v.hher else null,
                treinamento = if (temHher) v.hhTreinamento / v.hher else null
            )
        }
    }

    fun resumo(serie: List<RemIndicadores>): RemResumoAno {
        val total = serie.fold(RemValores()) { a, i -> a + i.valores }
        val comColab = serie.map { it.valores.colaboradores }.filter { it > 0 }
        val h = total.hher
        return RemResumoAno(
            total = total,
            mesesComDados = serie.count { it.valores.preenchido },
            mediaColaboradores = if (comColab.isEmpty()) 0.0 else comColab.average(),
            tfsa = taxa(total.lesaoSem, h),
            tfca = taxa(total.lesaoCom, h),
            tg = taxa(total.diasPerdidos, h),
            absenteismo = if (h > 0) total.horasAbsenteismo / h else null,
            treinamento = if (h > 0) total.hhTreinamento / h else null,
            ultimoMes = serie.lastOrNull { it.valores.preenchido }?.mes
        )
    }

    /** Conferências de preenchimento do mês (avisos, não bloqueiam o salvamento). */
    fun alertas(v: RemValores): List<String> {
        if (!v.preenchido) return emptyList()
        val out = mutableListOf<String>()
        if (v.hher <= 0) {
            out += "Informe o HHER: sem ele as taxas do mês ficam zeradas."
        } else if (v.colaboradores > 0 && v.hher / v.colaboradores > 320) {
            out += "HHER alto para o efetivo: ${RemFmt.num(v.hher / v.colaboradores, 0)} h por colaborador no mês."
        }
        if (v.colaboradores <= 0 && v.hher > 0) out += "Informe o número de colaboradores."
        if (v.horasAbsenteismo > v.hher && v.hher > 0) out += "Horas de absenteísmo maiores que o HHER."
        if (v.hhTreinamento > v.hher && v.hher > 0) out += "Homem-hora de treinamento maior que o HHER."
        if (v.diasTransportados > v.diasPerdidos) {
            out += "Lesão: dias transportados fazem parte dos dias perdidos no mês, não podem ser maiores."
        }
        if (v.trajDiasTransportados > v.trajDiasPerdidos) {
            out += "Trajeto: dias transportados fazem parte dos dias perdidos no mês, não podem ser maiores."
        }
        if (v.lesaoCom > 0 && v.diasPerdidos <= 0) out += "Há acidente com afastamento, mas nenhum dia perdido no mês."
        if (v.diasPerdidos > v.diasTransportados && v.lesaoCom <= 0) {
            out += "Dias perdidos sem acidente com afastamento no mês. Se o acidente foi em mês anterior, informe também em transportados."
        }
        if (v.lesaoCom + v.trajCom > v.cat) out += "Acidente com afastamento sem CAT correspondente (nº de CAT menor que os afastamentos)."
        return out
    }
}

/** Formatação pt-BR (vírgula decimal, ponto de milhar). */
object RemFmt {
    fun num(v: Double, casas: Int = 2): String {
        var fator = 1L
        repeat(casas) { fator *= 10 }
        val arred = (abs(v) * fator).roundToLong()
        val inteiro = (arred / fator).toString().reversed().chunked(3).joinToString(".").reversed()
        val dec = if (casas > 0) "," + (arred % fator).toString().padStart(casas, '0') else ""
        return (if (v < 0 && arred != 0L) "-" else "") + inteiro + dec
    }

    /** Inteiros sem casas; demais com até [casas]. */
    fun qtd(v: Double, casas: Int = 2): String = if (v % 1.0 == 0.0) num(v, 0) else num(v, casas)

    fun pct(v: Double?, casas: Int = 2): String = v?.let { num(it * 100, casas) + "%" } ?: "—"

    fun taxa(v: Double?): String = v?.let { num(it, 2) } ?: "—"
}
